/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useEffect, useRef, useState } from "react";

import { useSelector } from "react-redux";
import styled from "styled-components";

import { semverCompare, Plate } from "@vienna/common";

import clientPackage from "../../../../package.json";
import { Auth } from "src/classes/user/auth/Auth";
import { XButton } from "src/components/UI/Buttons/XButton";
import { ModalOverlay } from "src/components/UI/Modal/Modal";
import { AuthLevel } from "src/lib/constants";
import type { AppState } from "src/store";

export const WhatsNew = () => {
  const backgroundRef = useRef(null);

  const auth = Auth.instance;
  const authorizationLevel = auth.observe((state) => state.authLevel);
  const isScreenshotter = useSelector((state: AppState) => state.globalUI.uiSettings.isScreenshotter);

  const [isOpenPage, setIsOpenPage] = useState<Plate | null>(null);

  // check if a new version has notes on vht
  useEffect(() => {
    if (authorizationLevel === AuthLevel.Authorized) {
      const lastVersion = localStorage.getItem("mdSeen")?.replace("v", "") || "0.0.0";
      // there might be patch notes, check vht's plates
      if (semverCompare(lastVersion, clientPackage.version) === "less" && !isScreenshotter) {
        // api
        //   .get(
        //     `/changelog?${queryString.stringify({ lastSeen: lastVersion, currentVersion: clientPackage.version })}`
        //   )
        //   .then((res) => {
        //     setIsOpenPage(res as any);
        //   });
      }
    }
  }, []);

  const handleClose = () => {
    setIsOpenPage(null);
    localStorage.setItem("mdSeen", clientPackage.version);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (e.target === backgroundRef.current) {
      handleClose();
    }
  };

  if (!isOpenPage) {
    return <></>;
  }

  return (
    <ModalOverlay ref={backgroundRef} onClick={handleClick} style={{ paddingTop: "100px" }}>
      <StyledWhatsNewWrapper>
        <WNHeader>
          VIENNA 1.3 &quot;SEINE&quot;
          <CloseButtonWrapper>
            <XButton onClick={handleClose} />
          </CloseButtonWrapper>
        </WNHeader>
        <WNBody>
          <img src="https://vienna-dev.imgix.net/ef17fd50-1c4f-4625-b514-63efe07af06e.png?fit=max&h=690&w=550" />
        </WNBody>
      </StyledWhatsNewWrapper>
    </ModalOverlay>
  );
};

const StyledWhatsNewWrapper = styled.div`
  background: ${({ theme }) => theme.background};
  border: 2px solid ${({ theme }) => theme.primary};
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  max-height: 580px;
  max-width: 383px;
  overflow-y: scroll;
  box-shadow: ${({ theme }) => `10px 10px ${theme.subtleContrast}`};
  border: ${({ theme }) => `1px solid ${theme.modal.border}`};
`;

const WNHeader = styled.div`
  color: ${({ theme }) => theme.text.default};
  font-family: "IBM Plex Mono", monospace;
  font-size: 18px;
  font-weight: 700;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  padding-top: 35px;
  padding-bottom: 10px;
  height: 50px;
  width: 100%;
`;

const WNBody = styled.div`
  font-family: "Inter", sans-serif;
  padding-left: 72px;
  padding-right: 72px;
  padding-top: 40px;
  max-width: 550px;
  margin: 0 auto;
  img {
    width: 100%;
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 25px;
  right: 25px;
`;

export default WhatsNew;
